import { Fragment } from 'react';
import { CircleMarker, Marker, Popup } from 'react-leaflet';
import { geometryCentroid, normalizeGeometry } from '../../lib/geo';
import type { Farmbase } from '../../types/domain';
import GeometryShape from './GeometryShape';
import { labelChipIcon, OPS_COLORS } from './opsChips';

// Total manpower for the label sub-line; 0 when unset.
export function farmbasePax(fb: Farmbase): number {
  return (fb.manpowerOfficers ?? 0) + (fb.manpowerJCOs ?? 0) + (fb.manpowerORs ?? 0);
}

// "FB-3" → title "FARMBASE 3", code "FB-3"; other names render uppercase as-is.
function farmbaseLabel(fb: Farmbase): { title: string; sub?: string } {
  const nameUp = (fb.name || 'FARMBASE').toUpperCase();
  const m = nameUp.match(/^FB[\s-]?(\d+)$/);
  const pax = farmbasePax(fb);
  const paxStr = pax > 0 ? `${pax} pax` : '';
  const title = m ? `FARMBASE ${m[1]}` : nameUp;
  const sub = m ? [nameUp, paxStr].filter(Boolean).join(' · ') : paxStr || undefined;
  return { title, sub };
}

// F-1: every farmbase drawn as its geometry (cyan) + a permanent label chip.
// Fetching + unit scoping live in LiveMapPage (useOperationalMap) — this is a
// pure renderer so the same data also drives auto-fit bounds.
export default function FarmbaseLayer({ farmbases }: { farmbases: Farmbase[] }) {
  return (
    <>
      {farmbases.map((fb) => {
        const geom = normalizeGeometry(fb);
        if (!geom) return null;
        const rep =
          fb.repLat != null && fb.repLng != null
            ? { lat: fb.repLat, lon: fb.repLng }
            : geometryCentroid(geom);
        if (!rep) return null;
        const { title, sub } = farmbaseLabel(fb);

        // Per-object style (§16) overrides the default cyan when set; absent → today's look.
        const style = geom.style;
        const strokeColor = style?.strokeColor ?? OPS_COLORS.farmbase;
        const dotColor = style?.strokeColor ?? OPS_COLORS.farmbase;

        return (
          <Fragment key={fb.id}>
            <GeometryShape
              geometry={geom}
              color={strokeColor}
              fillColor={style?.fillColor}
              dashed={style?.dashed}
              weight={style?.weight}
              opacity={style?.opacity}
              fillOpacity={style?.fillOpacity ?? 0.08}
            />
            {geom.shape === 'POINT' && (
              <CircleMarker
                center={[rep.lat, rep.lon]}
                radius={4}
                pathOptions={{ color: dotColor, fillColor: dotColor, fillOpacity: 0.9, weight: 1 }}
              />
            )}
            <Marker
              position={[rep.lat, rep.lon]}
              icon={labelChipIcon({ title, sub, color: OPS_COLORS.farmbase })}
              zIndexOffset={850}
            >
              <Popup>
                <div className="p-1 min-w-[170px]">
                  <div className="font-bold">{fb.name}</div>
                  {fb.commanderName && <div className="text-xs text-gray-600">Comd: {fb.commanderName}</div>}
                  <div className="text-xs text-gray-600">
                    O {fb.manpowerOfficers ?? 0} · JCO {fb.manpowerJCOs ?? 0} · OR {fb.manpowerORs ?? 0}
                  </div>
                  <div className="text-[10px] font-mono text-gray-400 mt-1">
                    {rep.lat.toFixed(6)}, {rep.lon.toFixed(6)}
                  </div>
                </div>
              </Popup>
            </Marker>
          </Fragment>
        );
      })}
    </>
  );
}
